import React from 'react';

import { Grid, Typography, Button as BaseButton } from 'components/base';
import BaseIcon, { ICON_TYPE as BASE_ICON_TYPE } from 'components/icon';
import { getTotalPrice } from 'data/lib/product';

import useEditorContext from '../../context/editorContext';
import useEditorItemContext from '../../context/editorItemContext';
import usePhotoWellContext from '../../context/photoWellContext';
import usePhotoCropperContext from '../../context/photoCropperContext';
import {
  getEmptyPage,
  getOriginalPage,
  getPossiblePages,
  getNumberOfPages,
  getLayoutType,
  TEMPLATE_LAYOUT,
  getMaximumNumberOfPages,
  getMinimumNumberOfPages,
  getSampleBookPage,
  getTransformedPage,
} from 'editor-utils/template';
import pagesValidator from '../../lib/pagesValidator';
import { formatData } from 'editor-utils/dataParser';

import PageWrapper from '../../components/page/wrapper';
import Page from '../../components/page/preview';
import { Layout } from '../../components/rich';
import { generateUpdatedPage, generatePage } from '../../generator/utils';
import EditorLayout from '../components/layout';
import OptionedContinueButton from '../components/optionedContinueButton';
import TooltipPanel from '../components/tooltipPanel';

const BookPreview = props => {
  const editorContext = useEditorContext();
  const { item, product, template, config } = useEditorItemContext();

  const photoWell = usePhotoWellContext();
  const photoCropper = usePhotoCropperContext();

  const [cover, ...pages] = item.pages;
  const emptyPage = getEmptyPage(template);

  const numberOfPages = getNumberOfPages(template, item.pages);

  const handleOnSuccessClick = async (checkErrors = true) => {
    const validator = pagesValidator(template, item.pages, config, 'book');
    const data = formatData(template, item, { validator });

    if (validator.isValid() || !checkErrors) {
      props.onSuccess({ data });
      return { data };
    }

    const errors = validator.getErrors();
    props.onSuccess({ errors });
    return { errors };
  };

  let canAddMorePages = false;
  let canRemovePages = false;
  if (getLayoutType(template) === TEMPLATE_LAYOUT.AUTOMATIC) {
    canAddMorePages = numberOfPages < getMaximumNumberOfPages(template);
    canRemovePages = numberOfPages > getMinimumNumberOfPages(template);
  }

  const handleOnChangePage = (index, pageId) => {
    const newPage = generateUpdatedPage(
      template,
      item.pages[index],
      pageId,
      index === 0 ? 'cover' : 'pages',
    );

    editorContext.changePage(item.id, newPage, { pageIndex: index });
  };

  const handleOnPagePhotoUpload = (pageIndex, placeholder) => {
    photoWell.show({
      uploadEnabled: true,
      onSelectPhotos: selectedPhotos => {
        editorContext.addPhotoToPlaceholder(item.id, pageIndex, placeholder.id, selectedPhotos[0]);
        photoWell.hide();
      },
    });
  };

  const handleOnPagePhotoEdit = (pageIndex, placeholder, photo) => {
    photoCropper.show({
      placeholder,
      photo,
      onSuccess: data => {
        editorContext.setPhotoData(item.id, pageIndex, placeholder.id, data);
      },
    });
  };

  const handleOnPageTextChange = (pageIndex, placeholder, value, override) => {
    editorContext.setTextData(item.id, pageIndex, placeholder.id, value, override);
  };

  const handleOnAddPages = pageIndex => {
    const index = pageIndex + 1;

    editorContext.setPages(item.id, [
      ...item.pages.slice(0, index + 1),
      generatePage(getSampleBookPage(template)),
      generatePage(getSampleBookPage(template)),
      ...item.pages.slice(index + 1),
    ]);
  };

  const handleOnRemovePages = pageIndices => {
    editorContext.setPages(
      item.id,
      item.pages.filter((page, index) => pageIndices.indexOf(index - 1) < 0),
    );
  };

  let pageNumber = 0;

  const emptyPagePreview = (
    <PageWrapper page={emptyPage} fullWidth>
      <Page page={emptyPage} fullWidth />
    </PageWrapper>
  );

  const sideToolbars = [];

  sideToolbars.push(<TooltipPanel key="tooltip" />);

  return (
    <EditorLayout
      actionBar={props.actionBar}
      price={getTotalPrice(product, pages.length)}
      actionButton={
        <OptionedContinueButton onClick={handleOnSuccessClick}>Add to Cart</OptionedContinueButton>
      }
      toolbars={sideToolbars}
    >
      <Layout.Container mt={3} mb={3} pl={0} pr={0}>
        <div className="text-center mx-auto mw-800">
          <Grid.Row noGutters>
            <Grid.Col xs={6} className="col-page-preview mb-1" />
            <Grid.Col xs={6} className="col-page-preview mb-1">
              {(() => {
                const originalPage = getOriginalPage(template, cover, 'cover');

                return (
                  <PageWrapper
                    page={originalPage}
                    buttonPosition="right"
                    otherPages={template.cover.map(otherPage => ({
                      id: otherPage.id,
                      preview: (
                        <Page
                          page={getTransformedPage(template, originalPage, otherPage.id, 'cover')}
                          template={template}
                          photos={item.photos}
                          fullWidth
                          preview
                        />
                      ),
                    }))}
                    onChangePage={id => handleOnChangePage(0, id)}
                    fullWidth
                  >
                    <Page
                      page={originalPage}
                      photos={item.photos}
                      template={template}
                      onPhotoUpload={(...data) => handleOnPagePhotoUpload(0, ...data)}
                      onPhotoEdit={(...data) => handleOnPagePhotoEdit(0, ...data)}
                      onTextChange={(...data) => handleOnPageTextChange(0, ...data)}
                      fullWidth
                      interactive
                    />
                  </PageWrapper>
                );
              })()}
              <Typography.H6>Cover Page</Typography.H6>
            </Grid.Col>
          </Grid.Row>

          <Grid.Row noGutters>
            <Grid.Col xs={6} className="col-page-preview mb-1">
              {emptyPagePreview}
              <Typography.H6>&nbsp;</Typography.H6>
            </Grid.Col>
            {pages.map((page, index) => {
              const originalPage = getOriginalPage(template, page);

              const pageWrapper = (
                <PageWrapper
                  page={originalPage}
                  buttonPosition={index % 2 === 0 ? 'right' : 'left'}
                  otherPages={getPossiblePages(template).map(otherPage => ({
                    id: otherPage.id,
                    preview: (
                      <Page
                        page={getTransformedPage(template, originalPage, otherPage.id)}
                        template={template}
                        photos={item.photos}
                        fullWidth
                        preview
                      />
                    ),
                  }))}
                  onChangePage={id => handleOnChangePage(index + 1, id)}
                  fullWidth
                >
                  <Page
                    page={originalPage}
                    photos={item.photos}
                    template={template}
                    onPhotoUpload={(...data) => handleOnPagePhotoUpload(index + 1, ...data)}
                    onPhotoEdit={(...data) => handleOnPagePhotoEdit(index + 1, ...data)}
                    onTextChange={(...data) => handleOnPageTextChange(index + 1, ...data)}
                    fullWidth
                    interactive
                  />
                </PageWrapper>
              );

              pageNumber += 1;

              let removePageComponent;
              if (canRemovePages && pageNumber % 2 === 0 && index < pages.length - 1) {
                removePageComponent = (
                  <Grid.Col xs={12} className="editor-book-remove-page">
                    <BaseButton
                      className="btn-circle bg-white"
                      size="xs"
                      type="outline"
                      onClick={() =>
                        handleOnRemovePages(originalPage.span ? [index] : [index, index + 1])
                      }
                    >
                      <BaseIcon type={BASE_ICON_TYPE.TRASH} />
                    </BaseButton>
                  </Grid.Col>
                );
              }

              let addPageComponent;
              if (canAddMorePages && pageNumber % 2 === 1) {
                addPageComponent = (
                  <Grid.Col xs={12} className="mb-4">
                    <Typography.Span className="small-decorated">
                      <BaseButton
                        className="btn-circle bg-white"
                        size="xs"
                        type="outline"
                        onClick={() => handleOnAddPages(index)}
                      >
                        <BaseIcon type={BASE_ICON_TYPE.PLUS} />
                      </BaseButton>
                    </Typography.Span>
                  </Grid.Col>
                );
              }

              if (originalPage.span) {
                pageNumber += 1;
                return (
                  <React.Fragment key={page.key}>
                    {removePageComponent}
                    <Grid.Col xs={12} className="col-page-preview mb-1">
                      {pageWrapper}
                      <Grid.Row>
                        <Grid.Col xs={6}>
                          <Typography.H6>{`Page ${pageNumber - 1}`}</Typography.H6>
                        </Grid.Col>
                        <Grid.Col xs={6}>
                          <Typography.H6>{`Page ${pageNumber}`}</Typography.H6>
                        </Grid.Col>
                      </Grid.Row>
                    </Grid.Col>
                    {addPageComponent}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={page.key}>
                  {removePageComponent}
                  <Grid.Col xs={6} className="col-page-preview mb-1">
                    {pageWrapper}
                    <Typography.H6>{`Page ${pageNumber}`}</Typography.H6>
                  </Grid.Col>
                  {addPageComponent}
                </React.Fragment>
              );
            })}

            <Grid.Col xs={6} className="col-page-preview mb-1">
              {emptyPagePreview}
              <Typography.H6>&nbsp;</Typography.H6>
            </Grid.Col>
          </Grid.Row>

          <Grid.Row noGutters>
            <Grid.Col xs={6} className="col-page-preview mb-1">
              {(() => {
                const originalPage = getOriginalPage(template, {}, 'back');

                return (
                  <PageWrapper page={originalPage} fullWidth>
                    <Page page={originalPage} template={template} fullWidth />
                  </PageWrapper>
                );
              })()}
              <Typography.H6>Back Cover</Typography.H6>
            </Grid.Col>
            <Grid.Col xs={6} className="col-page-preview mb-1" />
          </Grid.Row>
        </div>
      </Layout.Container>
    </EditorLayout>
  );
};

export default BookPreview;
